import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import { Grid } from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddIcon from "@mui/icons-material/Add";

import { updateShippingOrder } from "src/api/shippingOrder";

import font from "src/styles/font";
import device from "src/styles/device";
import { checkNotEmpty, convertIntToCurrency } from "src/common";
import { Black, DarkGrey, Green, GreyStroke, Primary } from "src/styles/color";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import UploadPdf from "src/components/upload/dropzone/UploadPdf";
import UploadImage from "src/components/upload/dropzone/UploadImage";

import { SpanLabel } from "src/modules/order/shipping-order/components";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    color: ${Black};
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 640,
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: #d8d8dc;
  margin: 24px 0;
`;

const SpanTitle = styled.div`
  font-size: ${font.size.md};
  font-weight: ${({ slim }) => (slim ? "400" : "600")};
  line-height: 24px;
  margin: ${({ slim, unMargin }) => slim && !unMargin && "auto"};

  span {
    font-size: ${font.size.md};
    font-weight: 500;
    line-height: 24px;
    color: ${Primary};
  }

  @media ${device.mobileL} {
    font-size: ${font.size.sm};

    span {
      font-size: ${font.size.sm};
    }
  }
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-adornedEnd {
    padding-right: 2px;
  }
`;

const SumPrice = styled.h2`
  color: ${Primary};
  margin: 0 16px 0 0;
  font-weight: 600;
`;

const WrapperBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media ${device.mobileL} {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    .MuiFormControl-root {
      width: 100%;
      min-width: unset;
      max-width: unset;
      min-height: 40px;
    }
  }
`;

const StyledDatePicker = styled(DatePicker)`
  .MuiOutlinedInput-root {
    padding-right: 12px;
  }
  &.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${DarkGrey};
  }
  .Mui-disabled,
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${GreyStroke};
  }
`;

const WrapperUploadImage = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const WrapperImgSlip = styled.div`
  width: 100%;
  max-width: 300px;
  height: auto;
`;

const ImgSlip = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const rainbowAnimation = keyframes`
    0%,100% {
        background-position: 0 0;
    }

    50% {
        background-position: 100% 0;
    }
`;

const LabelAction = styled.div`
  padding: 4px 8px;
  min-height: 24px;
  width: 100%;
  margin-bottom: 8px;
  border-radius: 8px;
  font-size: 0.875rem;
  line-height: 16px;
  font-weight: 500;
  color: #f39c12;
  text-align: center;
  /* border: 1px solid ${Green}; */
  /* background: linear-gradient(
    to right,
    #6666ff,
    #0099ff,
    #00ff00,
    #ff3399,
    #6666ff
  );
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ${rainbowAnimation} 6s ease-in-out infinite;
  background-size: 400% 100%; */

  span {
    font-size: 0.875rem;
    line-height: 14px;
    font-weight: 600;
    color: ${Primary};
  }

  @media ${device.mobileL} {
    font-size: 0.75rem;
    line-height: 14px;

    span {
      font-size: 0.75rem;
    }
  }
`;

const PreparingDialog = ({ onClose, onSuccess, orderDetail, setIsLoading }) => {
  const { enqueueSnackbar } = useSnackbar();

  const initialDetail = {
    order: null,
    method: "ส่งไปถึงลูกค้าโดยตรง",
    fulfillmentProcess: [
      {
        type: "มีค่าจัดส่ง",
        carrier: "จัดส่งโดยร้านค้า",
        trackingNumber: null,
        trackingUrl: null,
        shippingFee: null,
        pickupAddress: {
          name: null,
          tel: null,
          address: null,
          coordinates: {
            lat: null,
            lng: null,
          },
        },
        shippingProofs: [],
        deliveryProofs: [],
        shippedDate: null,
        estimatedDeliveryDate: null,
        actualDeliveryDate: null,
        remark: null,
      },
    ],
    payment: {
      method: null,
      info: {
        bankAccountName: null,
        bankNo: null,
      },
      total: null,
      paymentDate: null,
      paymentProof: null,
    },
    status: {
      failureReason: null,
      value: "pending",
    },
    note: null,
    documents: {
      customer: {
        qt: {
          qtDate: null,
          qtNo: null,
          customerQtFile: null,
          customerQtAdditionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          customerInvFile: null,
          customerInvAdditionalFile: null,
        },
      },
      xsurface: {
        po: {
          poDate: null,
          poNo: null,
          xsurfacePoFile: null,
          xsurfacePoAdditionalFile: null,
        },
        vc: {
          vcDate: null,
          vcNo: null,
          xsurfaceVcFile: null,
          xsurfaceVcAdditionalFile: null,
        },
      },
      seller: {
        qt: {
          qtDate: null,
          qtNo: null,
          sellerQtFile: null,
          sellerQtAdditionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          sellerInvFile: null,
          sellerInvAdditionalFile: null,
        },
      },
    },
  };

  const [shippingDetail, setShippingDetail] = useState(initialDetail);

  useEffect(() => {
    if (!!orderDetail) {
      setShippingDetail((order) => ({ ...order, ...orderDetail }));
    }
  }, [orderDetail]);

  const renameFile = (file) => {
    const randomPart = Math.floor(Math.random() * 100000);
    let newName = `PO${dayjs().format("DDMMYY")}-${randomPart}`;
    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const payload = {
        ...shippingDetail,
        status: {
          value: "preparing",
        },
        order: orderDetail?.order?._id,
      };
      delete payload.orderDetail;

      const data = new FormData();
      for (const field in payload) {
        if (
          field === "order" ||
          field === "_id" ||
          field === "method" ||
          field === "note" ||
          field === "seller"
        ) {
          data.append(field, payload[field]);
        } else {
          data.append(field, JSON.stringify(payload[field]));
        }
      }

      let document = JSON.parse(data.get("documents"));
      delete document.xsurface.po.xsurfacePoFile;
      delete document.xsurface.po.xsurfacePoAdditionalFile;
      data.set("documents", JSON.stringify(document));

      if (
        typeof payload.documents.xsurface.po.xsurfacePoFile === "object" &&
        payload.documents.xsurface.po.xsurfacePoFile !== null
      ) {
        if (payload.documents.xsurface.po.xsurfacePoFile?.path) {
          data.append(
            "documents.xsurface.po.xsurfacePoFile",
            renameFile(payload.documents.xsurface.po.xsurfacePoFile),
          );
        } else {
          document.xsurface.po.xsurfacePoFile =
            payload.documents.xsurface.po.xsurfacePoFile._id;
          data.set("documents", JSON.stringify(document));
        }
      }

      if (
        typeof payload.documents.xsurface.po.xsurfacePoAdditionalFile ===
          "object" &&
        payload.documents.xsurface.po.xsurfacePoAdditionalFile !== null
      ) {
        if (payload.documents.xsurface.po.xsurfacePoAdditionalFile?.path) {
          data.append(
            "documents.xsurface.po.xsurfacePoAdditionalFile",
            renameFile(payload.documents.xsurface.po.xsurfacePoAdditionalFile),
          );
        } else {
          document.xsurface.po.xsurfacePoAdditionalFile =
            payload.documents.xsurface.po.xsurfacePoAdditionalFile._id;
          data.set("documents", JSON.stringify(document));
        }
      }

      if (
        shippingDetail?.method !== "ลูกค้ารับเอง" &&
        (shippingDetail?.fulfillmentProcess?.[0]?.type === "มีค่าจัดส่ง" ||
          shippingDetail?.fulfillmentProcess?.[1]?.type === "มีค่าจัดส่ง" ||
          (shippingDetail?.fulfillmentProcess?.[0]?.type === "โดย Xsurface" &&
            shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck?.result
              ?.totalWithDiscount > 0))
      )
        data.append("payment.paymentProof", payload.payment.paymentProof);
      // data.append("payment", JSON.stringify(payload.payment));
      delete data.documents;

      await updateShippingOrder(shippingDetail._id, data);
      await onSuccess();
      enqueueSnackbar(`อัพเดทรายละเอียดการจัดส่งสินค้า สำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      console.error("err:", err);
      enqueueSnackbar(err.response.data.message, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateField = () => {
    if (shippingDetail?.fulfillmentProcess.length > 1) {
      if (
        shippingDetail?.method !== "ลูกค้ารับเอง" &&
        (shippingDetail?.fulfillmentProcess?.[0]?.type === "มีค่าจัดส่ง" ||
          shippingDetail?.fulfillmentProcess?.[1]?.type === "มีค่าจัดส่ง")
      ) {
        if (
          shippingDetail?.payment?.paymentDate &&
          shippingDetail?.payment?.paymentProof &&
          shippingDetail?.documents?.xsurface?.po?.poDate &&
          shippingDetail?.documents?.xsurface?.po?.poNo &&
          shippingDetail?.documents?.xsurface?.po?.xsurfacePoFile
        )
          return false;
        else return true;
      } else {
        if (
          shippingDetail?.documents?.xsurface?.po?.poDate &&
          shippingDetail?.documents?.xsurface?.po?.poNo &&
          shippingDetail?.documents?.xsurface?.po?.xsurfacePoFile
        )
          return false;
        else return true;
      }
    } else {
      if (
        shippingDetail?.method !== "ลูกค้ารับเอง" &&
        (shippingDetail?.fulfillmentProcess?.[0]?.type === "มีค่าจัดส่ง" ||
          (shippingDetail?.fulfillmentProcess?.[0]?.type === "โดย Xsurface" &&
            shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck?.result
              ?.totalWithDiscount > 0))
      ) {
        if (
          shippingDetail?.payment?.paymentDate &&
          shippingDetail?.payment?.paymentProof &&
          shippingDetail?.documents?.xsurface?.po?.poDate &&
          shippingDetail?.documents?.xsurface?.po?.poNo &&
          shippingDetail?.documents?.xsurface?.po?.xsurfacePoFile
        )
          return false;
        else return true;
      } else {
        if (
          shippingDetail?.documents?.xsurface?.po?.poDate &&
          shippingDetail?.documents?.xsurface?.po?.poNo &&
          shippingDetail?.documents?.xsurface?.po?.xsurfacePoFile
        )
          return false;
        else return true;
      }
    }
  };

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: {
            sm: 3,
            xs: 2,
          },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          fontSize: {
            sm: font.size.lg,
            xs: font.size.md,
          },
          fontWeight: 600,
        }}
      >
        ค่าจัดส่งเพิ่มเติม
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        {shippingDetail?.method !== "ลูกค้ารับเอง" &&
        (shippingDetail?.fulfillmentProcess?.[0]?.type === "มีค่าจัดส่ง" ||
          shippingDetail?.fulfillmentProcess?.[1]?.type === "มีค่าจัดส่ง") ? (
          <>
            <Grid container xs={12}>
              <Grid item xs={8}>
                <SpanTitle slim>ค่าจัดส่ง</SpanTitle>
              </Grid>

              <Grid item xs={4} display="flex" justifyContent="flex-end">
                <SpanTitle slim unMargin>
                  {orderDetail?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" &&
                  orderDetail?.fulfillmentProcess[1]?.type === "มีค่าจัดส่ง"
                    ? `${convertIntToCurrency(orderDetail?.fulfillmentProcess[0]?.shippingFee + orderDetail?.fulfillmentProcess[1]?.shippingFee)}`
                    : orderDetail?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง"
                      ? convertIntToCurrency(
                          orderDetail?.fulfillmentProcess[0]?.shippingFee,
                        )
                      : orderDetail?.fulfillmentProcess[1]?.type ===
                          "มีค่าจัดส่ง"
                        ? convertIntToCurrency(
                            orderDetail?.fulfillmentProcess[1]?.shippingFee,
                          )
                        : null}
                </SpanTitle>
                <Grid item ml={2}>
                  <SpanTitle slim unMargin>
                    บาท
                  </SpanTitle>
                </Grid>
              </Grid>
            </Grid>

            <Grid container xs={12} mt={2}>
              <Grid item xs={8}>
                <SpanTitle>ค่าจัดส่งรวมทั้งหมด</SpanTitle>
              </Grid>

              <Grid
                item
                xs={4}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
              >
                <SumPrice>
                  {orderDetail?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง" &&
                  orderDetail?.fulfillmentProcess[1]?.type === "มีค่าจัดส่ง"
                    ? `${convertIntToCurrency(orderDetail?.fulfillmentProcess[0]?.shippingFee + orderDetail?.fulfillmentProcess[1]?.shippingFee)}`
                    : orderDetail?.fulfillmentProcess[0]?.type === "มีค่าจัดส่ง"
                      ? convertIntToCurrency(
                          orderDetail?.fulfillmentProcess[0]?.shippingFee,
                        )
                      : orderDetail?.fulfillmentProcess[1]?.type ===
                          "มีค่าจัดส่ง"
                        ? convertIntToCurrency(
                            orderDetail?.fulfillmentProcess[1]?.shippingFee,
                          )
                        : null}
                </SumPrice>
                <SpanTitle slim unMargin>
                  บาท
                </SpanTitle>
              </Grid>
            </Grid>

            <Line />

            <Grid item xs={12}>
              <WrapperBox>
                <SpanTitle slim unMargin>
                  วันที่ชำระเงิน <span>*</span>
                </SpanTitle>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <StyledDatePicker
                    inputFormat="DD-MM-YYYY"
                    value={shippingDetail?.payment?.paymentDate}
                    onChange={(value) => {
                      const newValue = shippingDetail;
                      newValue.payment.paymentDate = value.$d;
                      setShippingDetail({ ...newValue });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        sx={{
                          // my: 2,
                          maxWidth: 200,
                        }}
                        // error={!(updateOrder?.slipDate !== "")}
                        // helperText={
                        //   !!formErrors[`promo_start_time`]
                        //     ? formErrors[`promo_start_time`].message
                        //     : ""
                        // }
                      />
                    )}
                  />
                </LocalizationProvider>
              </WrapperBox>
            </Grid>

            <Grid container xs={12} mt={3} mb={5} justifyContent="center">
              <UploadImage
                callBackFile={(fileCallBlack) => {
                  if (fileCallBlack !== "") {
                    const newValue = shippingDetail;
                    newValue.payment.paymentProof = fileCallBlack;
                    setShippingDetail({ ...newValue });
                  }
                }}
                closeAlert={false}
              >
                {checkNotEmpty(shippingDetail?.payment?.paymentProof) ? (
                  <WrapperUploadImage>
                    <WrapperImgSlip>
                      <ImgSlip
                        src={shippingDetail?.payment?.paymentProof.fileData}
                        alt="paySlipImage"
                      />
                    </WrapperImgSlip>
                  </WrapperUploadImage>
                ) : (
                  <WrapperUploadImage>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<AddIcon />}
                    >
                      แนบหลักฐานการชำระเงิน
                    </Button>
                  </WrapperUploadImage>
                )}
              </UploadImage>

              {/* {checkNotEmpty(shippingDetail?.payment?.paymentProof) && (
            <Grid container xs={12} justifyContent="center" mt={2}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<SaveAltIcon />}
              >
                ดาวน์โหลด
              </Button>
            </Grid>
          )} */}
            </Grid>

            <Line />
          </>
        ) : (
          shippingDetail?.fulfillmentProcess?.[0]?.type === "โดย Xsurface" &&
          shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck?.result
            ?.totalWithDiscount > 0 && (
            <>
              <Grid container xs={12}>
                <Grid item xs={8}>
                  <SpanTitle slim>ค่าจัดส่ง Lalamove</SpanTitle>
                </Grid>

                <Grid item xs={4} display="flex" justifyContent="flex-end">
                  <SpanTitle slim unMargin>
                    {convertIntToCurrency(
                      orderDetail?.fulfillmentProcess[0]?.lalamoveCheck?.result
                        ?.totalWithFee,
                    )}
                  </SpanTitle>
                  <Grid item ml={2}>
                    <SpanTitle slim unMargin>
                      บาท
                    </SpanTitle>
                  </Grid>
                </Grid>
              </Grid>

              <Grid container xs={12} mt={2} alignItems={"center"}>
                <Grid item xs={8}>
                  <SpanTitle>ยอดเรียกชำระเพิ่มเติม</SpanTitle>
                </Grid>

                <Grid
                  item
                  xs={4}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <SumPrice>
                    {convertIntToCurrency(
                      orderDetail?.fulfillmentProcess[0]?.lalamoveCheck?.result
                        ?.totalWithDiscount,
                    )}
                  </SumPrice>
                  <SpanTitle slim unMargin>
                    บาท
                  </SpanTitle>
                </Grid>
              </Grid>

              <Line />

              <Grid item xs={12}>
                <WrapperBox>
                  <SpanTitle slim unMargin>
                    วันที่ชำระเงิน <span>*</span>
                  </SpanTitle>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <StyledDatePicker
                      inputFormat="DD-MM-YYYY"
                      value={shippingDetail?.payment?.paymentDate}
                      onChange={(value) => {
                        const newValue = shippingDetail;
                        newValue.payment.paymentDate = value.$d;
                        setShippingDetail({ ...newValue });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          sx={{
                            // my: 2,
                            maxWidth: 200,
                          }}
                          // error={!(updateOrder?.slipDate !== "")}
                          // helperText={
                          //   !!formErrors[`promo_start_time`]
                          //     ? formErrors[`promo_start_time`].message
                          //     : ""
                          // }
                        />
                      )}
                    />
                  </LocalizationProvider>
                </WrapperBox>
              </Grid>

              <Grid container xs={12} mt={3} mb={5} justifyContent="center">
                <UploadImage
                  callBackFile={(fileCallBlack) => {
                    if (fileCallBlack !== "") {
                      const newValue = shippingDetail;
                      newValue.payment.paymentProof = fileCallBlack;
                      setShippingDetail({ ...newValue });
                    }
                  }}
                  closeAlert={false}
                >
                  {checkNotEmpty(shippingDetail?.payment?.paymentProof) ? (
                    <WrapperUploadImage>
                      <WrapperImgSlip>
                        <ImgSlip
                          src={shippingDetail?.payment?.paymentProof.fileData}
                          alt="paySlipImage"
                        />
                      </WrapperImgSlip>
                    </WrapperUploadImage>
                  ) : (
                    <WrapperUploadImage>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<AddIcon />}
                      >
                        แนบหลักฐานการชำระเงิน
                      </Button>
                    </WrapperUploadImage>
                  )}
                </UploadImage>

                {/* {checkNotEmpty(shippingDetail?.payment?.paymentProof) && (
                  <Grid container xs={12} justifyContent="center" mt={2}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<SaveAltIcon />}
                    >
                      ดาวน์โหลด
                    </Button>
                  </Grid>
                )} */}
              </Grid>

              <Line />
            </>
          )
        )}

        <Grid container spacing={{ mb: 2, xs: 1 }} mb={2}>
          <Grid item xs={12}>
            <SpanTitle>เอกสาร</SpanTitle>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={6} pr={2}>
            <SpanLabel isMargin>
              วันที่ PO <span>*</span>
            </SpanLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StyledDatePicker
                inputFormat="DD-MM-YYYY"
                value={shippingDetail?.documents?.xsurface?.po?.poDate}
                onChange={(value) => {
                  const newValue = shippingDetail;
                  newValue.documents.xsurface.po.poDate =
                    value !== null ? value.$d : "";
                  setShippingDetail({ ...newValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    sx={{
                      // my: 2,
                      width: "100%",
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={6} pl={2}>
            <SpanLabel isMargin>
              PO No. <span>*</span>
            </SpanLabel>
            <TextField
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={shippingDetail?.documents?.xsurface?.po?.poNo}
              onChange={({ target }) => {
                const newValue = shippingDetail;
                newValue.documents.xsurface.po.poNo = target.value;
                setShippingDetail({ ...newValue });
              }}
            />
          </Grid>
        </Grid>

        <Grid container mt={2}>
          <Grid item xs={12}>
            <SpanLabel isMargin>
              {`ใบ PO`} <span>*</span>
            </SpanLabel>
            <StyledTextField
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <>
                    {checkNotEmpty(
                      shippingDetail?.documents?.xsurface?.po?.xsurfacePoFile,
                    ) ? (
                      <IconButton
                        onClick={() => {
                          const newShipping = shippingDetail;
                          shippingDetail.documents.xsurface.po.xsurfacePoFile =
                            "";
                          setShippingDetail({ ...newShipping });
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{
                            color: Primary,
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                    ) : (
                      <UploadPdf
                        callBackFile={(fileCallBlack) => {
                          const newShipping = shippingDetail;
                          newShipping.documents.xsurface.po.xsurfacePoFile =
                            fileCallBlack;
                          setShippingDetail({ ...newShipping });
                        }}
                        closeAlert={false}
                      >
                        <IconButton>
                          <AttachFileIcon
                            sx={{
                              color: Black,
                              cursor: "pointer",
                              fontSize: font.size.lg,
                              ":hover": {
                                color: "#e04132",
                              },
                            }}
                          />
                        </IconButton>
                      </UploadPdf>
                    )}
                  </>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              value={
                typeof shippingDetail?.documents?.xsurface?.po
                  ?.xsurfacePoFile !== "object"
                  ? shippingDetail?.documents?.xsurface?.po?.xsurfacePoFile
                  : !!shippingDetail?.documents?.xsurface?.po?.xsurfacePoFile
                        ?.docUrl
                    ? shippingDetail?.documents?.xsurface?.po?.xsurfacePoFile
                        ?.originalFileName
                    : shippingDetail?.documents?.xsurface?.po?.xsurfacePoFile
                        ?.name
              }
            />
          </Grid>
        </Grid>

        <Grid container mt={2}>
          <Grid item xs={12}>
            <SpanLabel isMargin>{`เอกสารเพิ่มเติม`}</SpanLabel>
            <StyledTextField
              variant="outlined"
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <>
                    {checkNotEmpty(
                      shippingDetail?.documents?.xsurface?.po
                        ?.xsurfacePoAdditionalFile,
                    ) ? (
                      <IconButton
                        onClick={() => {
                          const newShipping = shippingDetail;
                          shippingDetail.documents.xsurface.po.xsurfacePoAdditionalFile =
                            "";
                          setShippingDetail({ ...newShipping });
                        }}
                      >
                        <DeleteOutlineIcon
                          sx={{
                            color: Primary,
                            cursor: "pointer",
                          }}
                        />
                      </IconButton>
                    ) : (
                      <UploadPdf
                        callBackFile={(fileCallBlack) => {
                          const newShipping = shippingDetail;
                          newShipping.documents.xsurface.po.xsurfacePoAdditionalFile =
                            fileCallBlack;
                          setShippingDetail({ ...newShipping });
                        }}
                        closeAlert={false}
                      >
                        <IconButton>
                          <AttachFileIcon
                            sx={{
                              color: Black,
                              cursor: "pointer",
                              fontSize: font.size.lg,
                              ":hover": {
                                color: "#e04132",
                              },
                            }}
                          />
                        </IconButton>
                      </UploadPdf>
                    )}
                  </>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              value={
                typeof shippingDetail?.documents?.xsurface?.po
                  ?.xsurfacePoAdditionalFile !== "object"
                  ? shippingDetail?.documents?.xsurface?.po
                      ?.xsurfacePoAdditionalFile
                  : !!shippingDetail?.documents?.xsurface?.po
                        ?.xsurfacePoAdditionalFile?.docUrl
                    ? shippingDetail?.documents?.xsurface?.po
                        ?.xsurfacePoAdditionalFile?.originalFileName
                    : shippingDetail?.documents?.xsurface?.po
                        ?.xsurfacePoAdditionalFile?.name
              }
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexWrap: "wrap" }}>
        {(shippingDetail?.method === "ลูกค้ารับเอง" ||
          (shippingDetail?.fulfillmentProcess?.[0]?.type !== "มีค่าจัดส่ง" &&
            shippingDetail?.fulfillmentProcess?.[0]?.type !== "โดย Xsurface" &&
            shippingDetail?.fulfillmentProcess?.[1]?.type !== "มีค่าจัดส่ง") ||
          (shippingDetail?.fulfillmentProcess?.[0]?.type === "โดย Xsurface" &&
            shippingDetail?.fulfillmentProcess[0]?.lalamoveCheck?.result
              ?.totalWithDiscount === 0)) &&
          !!shippingDetail?.documents?.xsurface?.po?.xsurfacePoFile?.docUrl && (
            <LabelAction>
              <span>*</span> ออเดอร์นี้ไม่มีค่าจัดส่ง แอดมินสามารถกด{" "}
              <span>"ยืนยัน"</span> เพื่อเปลี่ยนสถานะได้เลยค่ะ <span>*</span>
            </LabelAction>
          )}

        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => onClose()}
          // fullWidth
          sx={{ width: { md: "100%", xs: "calc(50% - 12px)" }, maxWidth: 180 }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={() => onSubmit()}
          fullWidth
          sx={{ width: { md: "100%", xs: "calc(50% - 12px)" }, maxWidth: 180 }}
          disabled={validateField()}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default PreparingDialog;
